import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconStatusChange } from '@tabler/icons-react';
import { CrudMode, CrudSchemaField, type EnsoFormItem } from '@ohpen/enso-ui';

import { TabProps } from '../interfaces';

export const Status: React.FC<TabProps> = ({ form, mode, selectsOptions, defaultComplianceStatus }) => {
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;

  useEffect(() => {
    if (mode !== CrudMode.CREATE) return;
    if (defaultComplianceStatus) {
      const current = form?.getFieldValue(['status', 'complianceStatus']);
      if (current === undefined) {
        form?.setFieldValue(['status', 'complianceStatus'], defaultComplianceStatus);
      }
    }
    if (form?.getFieldValue(['status', 'reviewRequired']) === undefined) {
      form?.setFieldValue(['status', 'reviewRequired'], false);
    }
    if (form?.getFieldValue(['status', 'onTheWatchlist']) === undefined) {
      form?.setFieldValue(['status', 'onTheWatchlist'], false);
    }
  }, [mode, defaultComplianceStatus, form]);

  const columns: EnsoFormItem[] = useMemo(
    () => [
      {
        title: t('lender.firms.labels.compliance'),
        dataIndex: ['status', 'complianceStatus'],
        valueType: 'radio',
        readonly,
        formItemProps: {
          rules: [{ required: true }],
        },
        fieldProps: {
          options: [
            { label: t('lender.firms.labels.pending'), value: 'PENDING' },
            { label: t('lender.firms.labels.notRequired'), value: 'NOT_REQUIRED' },
            { label: t('lender.firms.labels.failed'), value: 'FAILED' },
            { label: t('lender.firms.labels.passed'), value: 'PASSED' },
          ],
        },
        colProps: { span: 24 },
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.complianceReReviewRequired'),
            dataIndex: ['status', 'reviewRequired'],
            valueType: 'radio',
            initialValue: mode === CrudMode.CREATE ? false : undefined,
            readonly,
            fieldProps: {
              options: [
                { label: t('global.labels.yes'), value: true },
                { label: t('global.labels.no'), value: false },
              ],
            },
            formItemProps: {
              rules: [{ required: true }],
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.watchlist'),
            dataIndex: ['status', 'onTheWatchlist'],
            valueType: 'radio',
            initialValue: mode === CrudMode.CREATE ? false : undefined,
            readonly,
            fieldProps: {
              options: [
                { label: t('global.labels.yes'), value: true },
                { label: t('global.labels.no'), value: false },
              ],
            },
            formItemProps: {
              rules: [{ required: true }],
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        title: t('lender.firms.labels.complianceRecord'),
        valueType: 'group',
        colProps: { span: 24 },
        columns: [
          {
            title: t('lender.firms.labels.addedToPanel'),
            dataIndex: ['status', 'addedToPanel'],
            valueType: 'date',
            readonly: true,
            colProps: { span: 6 },
          },
          {
            title: t('global.labels.addedBy'),
            dataIndex: ['status', 'addedBy'],
            valueType: 'text',
            readonly: true,
            colProps: { span: 6 },
          },
          {
            title: t('global.labels.lastUpdate'),
            dataIndex: ['status', 'lastUpdate'],
            valueType: 'date',
            readonly: true,
            colProps: { span: 6 },
          },
          {
            title: t('global.labels.updatedBy'),
            dataIndex: ['status', 'updatedBy'],
            valueType: 'text',
            readonly: true,
            colProps: { span: 6 },
          },
        ],
      },
    ],
    [readonly, mode, t],
  );

  const schema: EnsoFormItem = useMemo(
    () => ({
      valueType: 'group',
      variant: 'card',
      icon: <IconStatusChange />,
      title: t('lender.firms.labels.status'),
      columns,
    }),
    [columns, t],
  );

  return <CrudSchemaField item={schema} form={form} readonly={readonly} />;
};
